import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAPI } from "../Context/MyContext";

/* ─── Icons ──── */
const Ico = {
  Star: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
  Layers: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>,
  Msg: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>,
  Alert: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
  Check: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><polyline points="20 6 9 17 4 12" /></svg>,
  Info: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>,
  Shield: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  Clock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-3.5 h-3.5"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
};

/* ─── Card wrapper ──── */
const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${className}`}>
    {children}
  </div>
);

/* ─── Section header inside card ───── */
const CardHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2.5 mb-5">
    <div className="text-slate-400"><Icon /></div>
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">{title}</p>
  </div>
);

/* ─── Status chip ───── */
const Badge = ({ color, children }) => {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50   text-amber-700   border-amber-200",
    red: "bg-red-50     text-red-700     border-red-200",
    indigo: "bg-indigo-50  text-indigo-700  border-indigo-200",
    slate: "bg-slate-100  text-slate-600   border-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${map[color]}`}>
      {children}
    </span>
  );
};

/* ─── Radial ring  ──── */
const Ring = ({ pct, color }) => {
  const r = 30, circ = 2 * Math.PI * r;
  const hex = { emerald: "#10b981", amber: "#f59e0b", red: "#ef4444" }[color] || "#10b981";
  return (
    <svg width="76" height="76" className="-rotate-90">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#f1f5f9" strokeWidth="5" />
      <circle cx="38" cy="38" r={r} fill="none" stroke={hex} strokeWidth="5"
        strokeDasharray={`${(pct / 100) * circ} ${circ}`} strokeLinecap="round" />
    </svg>
  );
};

/* ─── Skeleton Components ──── */
// const SkeletonBanner = () => (
//   <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-4 animate-pulse">
//     <div className="shrink-0 w-9 h-9 rounded-xl bg-slate-200" />
//     <div className="flex-1 min-w-0 space-y-2">
//       <div className="h-4 bg-slate-200 rounded w-32" />
//       <div className="h-3 bg-slate-200 rounded w-96 max-w-full" />
//     </div>
//     <div className="h-7 w-16 bg-slate-200 rounded-lg" />
//   </div>
// );

const SkeletonQualityCard = () => (
  <Card className="p-6 animate-pulse">
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-4 h-4 bg-slate-200 rounded" />
      <div className="h-3 bg-slate-200 rounded w-32" />
    </div>
    
    <div className="flex items-center gap-5">
      {/* Ring */}
      <div className="shrink-0 w-[76px] h-[76px] rounded-full bg-slate-200" />
      
      {/* Content */}
      <div className="flex-1 space-y-3">
        <div className="h-7 bg-slate-200 rounded w-16" />
        <div className="flex gap-1">
          <div className="w-4 h-4 bg-slate-200 rounded" />
          <div className="w-4 h-4 bg-slate-200 rounded" />
          <div className="w-4 h-4 bg-slate-200 rounded" />
        </div>
        <div className="h-3 bg-slate-200 rounded w-full" />
        <div className="h-3 bg-slate-200 rounded w-3/4" />
      </div>
    </div>
  </Card>
);

const SkeletonTierCard = () => (
  <Card className="p-6 animate-pulse">
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-4 h-4 bg-slate-200 rounded" />
      <div className="h-3 bg-slate-200 rounded w-32" />
    </div>
    
    {/* Tier value */}
    <div className="flex items-baseline gap-2 mb-6">
      <div className="h-9 bg-slate-200 rounded w-20" />
      <div className="h-4 bg-slate-200 rounded w-32" />
    </div>
    
    {/* Tier stepper */}
    <div className="mb-4">
      <div className="h-0.5 bg-slate-100 mb-3" />
      <div className="flex justify-between">
        <div className="flex flex-col items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200" />
          <div className="h-3 bg-slate-200 rounded w-8" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200" />
          <div className="h-3 bg-slate-200 rounded w-8" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200" />
          <div className="h-3 bg-slate-200 rounded w-8" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200" />
          <div className="h-3 bg-slate-200 rounded w-8" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200" />
          <div className="h-3 bg-slate-200 rounded w-8" />
        </div>
      </div>
    </div>
    
    <div className="h-3 bg-slate-200 rounded w-48 mx-auto mt-2" />
  </Card>
);

const SkeletonUsageCard = () => (
  <Card className="p-6 animate-pulse">
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-4 h-4 bg-slate-200 rounded" />
      <div className="h-3 bg-slate-200 rounded w-36" />
    </div>
    
    {/* Top row */}
    <div className="flex items-center justify-between mb-5">
      <div className="space-y-2">
        <div className="flex items-baseline gap-1.5">
          <div className="h-9 bg-slate-200 rounded w-12" />
          <div className="h-4 bg-slate-200 rounded w-16" />
        </div>
        <div className="h-7 bg-slate-200 rounded w-24" />
      </div>
      
      {/* Ring */}
      <div className="flex flex-col items-center gap-1">
        <div className="w-[76px] h-[76px] rounded-full bg-slate-200" />
        <div className="h-3 bg-slate-200 rounded w-16" />
      </div>
    </div>
    
    {/* Progress bar */}
    <div className="h-2 rounded-full bg-slate-100 mb-1.5" />
    <div className="flex justify-between mb-4">
      <div className="h-2.5 bg-slate-200 rounded w-4" />
      <div className="h-2.5 bg-slate-200 rounded w-8" />
    </div>
    
    {/* Bottom box */}
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
      <div className="space-y-1">
        <div className="h-2.5 bg-slate-200 rounded w-32" />
        <div className="h-4 bg-slate-200 rounded w-40" />
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3.5 h-3.5 bg-slate-200 rounded" />
        <div className="h-3 bg-slate-200 rounded w-20" />
      </div>
    </div>
  </Card>
);

const SkeletonWarningsCard = () => (
  <Card className="p-6 animate-pulse">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2.5">
        <div className="w-4 h-4 bg-slate-200 rounded" />
        <div className="h-3 bg-slate-200 rounded w-36" />
      </div>
      <div className="h-7 bg-slate-200 rounded w-20" />
    </div>
    
    {/* Warning items */}
    <div className="space-y-2.5">
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-1.5 w-2 h-2 rounded-full bg-slate-300" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-3 bg-slate-200 rounded w-20" />
              <div className="h-6 bg-slate-200 rounded w-16" />
            </div>
            <div className="h-3 bg-slate-200 rounded w-full" />
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-1.5 w-2 h-2 rounded-full bg-slate-300" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-3 bg-slate-200 rounded w-20" />
              <div className="h-6 bg-slate-200 rounded w-16" />
            </div>
            <div className="h-3 bg-slate-200 rounded w-4/5" />
          </div>
        </div>
      </div>
    </div>
  </Card>
);

const SkeletonPage = () => (
  <div className="space-y-6 max-w-5xl mx-auto">
    {/* Header - Keep visible */}
    <div className="flex items-start justify-between">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Quality & Messaging Limits</h1>
        <p className="text-sm text-slate-500 mt-1">Monitor WhatsApp reliability metrics for this phone number</p>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
        Live
      </div>
    </div>

    {/* Banner */}
    {/* <SkeletonBanner /> */}

    {/* Row 1 */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SkeletonQualityCard />
      <SkeletonTierCard />
    </div>

    {/* Row 2 */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SkeletonUsageCard />
      <SkeletonWarningsCard />
    </div>
  </div>
);



/* ═════════════════════════════════════ 1. Campaign Readiness Banner  ═══════════════════════════════════════ */


// const CampaignBanner = ({ rating, usedPct, warningCount }) => {
//   const canSend = rating === "High" && usedPct < 80 && warningCount === 0;
//   const caution = !canSend && rating !== "Low" && usedPct < 90;

//   const cfg = canSend
//     ? { color: "emerald", Icon: Ico.Check, label: "Safe to Send", sub: "Quality, capacity, and rate limits are all healthy. You can send campaigns today." }
//     : caution
//       ? { color: "amber", Icon: Ico.Info, label: "Send with Caution", sub: "Some metrics are degraded. Reduce campaign volume to avoid WhatsApp restrictions." }
//       : { color: "red", Icon: Ico.Shield, label: "Do Not Send", sub: "Critical conditions detected. Resolve all warnings before launching campaigns." };

//   const styles = {
//     emerald: { wrap: "bg-emerald-50 border-emerald-200", icon: "bg-emerald-100 text-emerald-600", title: "text-emerald-800", sub: "text-emerald-700", dot: "bg-emerald-500" },
//     amber: { wrap: "bg-amber-50  border-amber-200", icon: "bg-amber-100  text-amber-600", title: "text-amber-800", sub: "text-amber-700", dot: "bg-amber-400" },
//     red: { wrap: "bg-red-50    border-red-200", icon: "bg-red-100    text-red-600", title: "text-red-800", sub: "text-red-700", dot: "bg-red-500" },
//   };
//   const s = styles[cfg.color];

//   return (
//     <div className={`rounded-2xl border px-5 py-4 flex items-center gap-4 ${s.wrap}`}>
//       <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${s.icon}`}>
//         <cfg.Icon />
//       </div>
//       <div className="flex-1 min-w-0">
//         <div className="flex items-center gap-2 mb-0.5">
//           <p className={`text-sm font-bold ${s.title}`}>{cfg.label}</p>
//           <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
//         </div>
//         <p className={`text-xs leading-relaxed ${s.sub}`}>{cfg.sub}</p>
//       </div>
//       <Badge color={cfg.color}>{cfg.label.split(" ")[0]}</Badge>
//     </div>
//   );
// };

/* ═════════════════════════════════════ 2. Quality Rating Card ═══════════════════════════════════════ */

const QualityRatingCard = ({ rating }) => {
  const cfg = {
    High: { color: "emerald", stars: 3, pct: 100, desc: "Excellent delivery & engagement. You're clear to run campaigns." },
    Medium: { color: "amber", stars: 2, pct: 60, desc: "Moderate performance. Avoid bulk messaging to prevent further degradation." },
    Low: { color: "red", stars: 1, pct: 25, desc: "Poor quality. Sending campaigns now risks getting blocked by WhatsApp." },
  };
  const c = cfg[rating] || cfg.High;
  const textMap = { emerald: "text-emerald-600", amber: "text-amber-600", red: "text-red-600" };

  return (
    <Card className="p-6">
      <CardHeader icon={Ico.Star} title="Quality Rating" />
      <div className="flex items-center gap-5">
        {/* Ring */}
        <div className="relative shrink-0">
          <Ring pct={c.pct} color={c.color} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-xs font-bold ${textMap[c.color]}`}>{c.pct}%</span>
          </div>
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="mb-2.5">
            <Badge color={c.color}>{rating}</Badge>
          </div>
          {/* Stars */}
          <div className="flex gap-1 mb-3">
            {[1, 2, 3].map(s => (
              <svg key={s} viewBox="0 0 24 24"
                className={`w-4 h-4 ${s <= c.stars ? textMap[c.color] : "text-slate-200"}`}
                fill={s <= c.stars ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5}>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            ))}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">{c.desc}</p>
        </div>
      </div>
    </Card>
  );
};

/* ════════════════════════════════════  3. Messaging Tier Card  ════════════════════════════════════════ */


const MessagingTierCard = ({ tier }) => {
  const tiers = ["250", "2K", "10K", "100K", "∞"];

  // Convert tier string to numeric value
  const tierValue = tier === "∞" ? Infinity : parseInt(tier.replace("K", "000"));

  // Numeric values for tiers
  const tierNumbers = tiers.map(t => (t === "∞" ? Infinity : parseInt(t.replace("K", "000"))));

  // Calculate progress % so it aligns with the circles
  const maxNumber = 100000; // 100K for calculation
  let progressPct = 0;

  if (tierValue === Infinity) {
    progressPct = 100;
  } else if (tierValue <= tierNumbers[0]) {
    progressPct = (tierValue / tierNumbers[0]) * (100 / (tiers.length - 1));
  } else {
    // Find the segment where tierValue belongs
    for (let i = 1; i < tierNumbers.length; i++) {
      const lower = tierNumbers[i - 1];
      const upper = tierNumbers[i] === Infinity ? maxNumber : tierNumbers[i];
      if (tierValue <= upper) {
        const segmentPct = ((tierValue - lower) / (upper - lower)) * (100 / (tiers.length - 1));
        progressPct = ((i - 1) * 100) / (tiers.length - 1) + segmentPct;
        break;
      }
    }
  }

  return (
    <Card className="p-6">
      <CardHeader icon={Ico.Layers} title="Messaging Tier" />

      {/* Display main tier value */}
      <div className="flex items-baseline gap-2 mb-6">
        <span className="text-3xl font-bold text-slate-900">{tier}</span>
        <span className="text-sm text-slate-400">messages / day</span>
      </div>

      {/* Tier stepper with dynamic progress */}
      <div className="relative mb-4">
        {/* Background line */}
        <div className="absolute top-3.5 left-0 right-0 h-0.5 bg-slate-100" />
        {/* Progress line */}
        <div
          className="absolute top-3.5 left-0 h-0.5 bg-indigo-400 transition-all"
          style={{ width: `${progressPct}%` }}
        />

        {/* Tier circles */}
        <div className="relative flex justify-between">
          {tiers.map((t, i) => {
            const reached = tierValue >= tierNumbers[i];
            return (
              <div key={t} className="flex flex-col items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all
                    ${tierValue === tierNumbers[i]
                      ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
                      : reached
                        ? "bg-indigo-100 border-indigo-400 text-indigo-600"
                        : "bg-white border-slate-200 text-slate-400"}`}
                >
                  {tierValue === tierNumbers[i]
                    ? <Ico.Check />
                    : t === "∞"
                      ? "∞"
                      : t.replace("K", "")}
                </div>
                <span className={`text-[10px] font-semibold ${reached ? "text-indigo-600" : "text-slate-400"}`}>{t}</span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-2 text-center">
        Current tier · up to <span className="font-semibold text-slate-600">{tier} conversations/day</span>
      </p>
    </Card>
  );
};

/* ════════════════════════════════════  4. Usage Card  ════════════════════════════════════════ */

const UsageCard = ({ used, limit }) => {
  const hasLimit = limit > 0;

  const safeUsed = hasLimit ? Math.min(used, limit) : 0;
  const safeRemaining = hasLimit ? Math.max(limit - safeUsed, 0) : 0;

  const pct = hasLimit ? (safeUsed / limit) * 100 : 0;
  const remPct = hasLimit ? 100 - pct : 100;

  const color = !hasLimit
    ? "slate"
    : pct >= 90
      ? "red"
      : pct >= 70
        ? "amber"
        : "emerald";

  const barHex = {
    red: "#ef4444",
    amber: "#f59e0b",
    emerald: "#10b981",
    slate: "#cbd5f5",
  }[color];

  const textCls = {
    red: "text-red-600",
    amber: "text-amber-600",
    emerald: "text-emerald-600",
    slate: "text-slate-500",
  }[color];

  const divCls = {
    red: "bg-red-50 border-red-100",
    amber: "bg-amber-50 border-amber-100",
    emerald: "bg-emerald-50 border-emerald-100",
    slate: "bg-slate-50 border-slate-200",
  }[color];

  return (
    <Card className="p-6">
      <CardHeader icon={Ico.Msg} title="Daily Conversations" />

      {/* Top row */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-baseline gap-1.5 mb-1.5">
            <span className="text-3xl font-bold text-slate-900">
              {safeUsed.toLocaleString()}
            </span>
            <span className="text-sm text-slate-400">
              / {limit.toLocaleString()}
            </span>
          </div>

          <Badge color={color}>
            {hasLimit ? `${pct.toFixed(1)}% used` : "No daily limit"}
          </Badge>
        </div>

        {/* Ring */}
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <Ring pct={remPct} color={color} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-xs font-bold ${textCls}`}>
                {hasLimit ? `${remPct.toFixed(0)}%` : "—"}
              </span>
              <span className="text-[9px] text-slate-400 mt-0.5">
                left
              </span>
            </div>
          </div>
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">
            Remaining
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1.5">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: barHex }}
        />
      </div>

      <div className="flex justify-between text-[10px] text-slate-400 mb-4">
        <span>0</span>
        <span>{limit.toLocaleString()}</span>
      </div>

      {/* Remaining row */}
      <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${divCls}`}>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">
            Remaining Capacity
          </p>
          <p className={`text-sm font-bold ${textCls}`}>
            {hasLimit
              ? `${safeRemaining.toLocaleString()} conversations left`
              : "No daily conversations allowed"}
          </p>
        </div>

        <div className="flex items-center gap-1.5 text-slate-400 text-[11px]">
          <Ico.Clock />
          <span>resets midnight</span>
        </div>
      </div>
    </Card>
  );
};


/* ════════════════════════════════════  5. Rate Limit Warnings  ════════════════════════════════════════ */

const RateLimitWarnings = ({ warnings }) => {
  const sevCfg = {
    critical: { badge: "red", dot: "bg-red-500", text: "text-red-700", sub: "text-red-500", row: "bg-red-50 border-red-100" },
    warning: { badge: "amber", dot: "bg-amber-400", text: "text-amber-700", sub: "text-amber-500", row: "bg-amber-50 border-amber-100" },
    info: { badge: "slate", dot: "bg-slate-400", text: "text-slate-700", sub: "text-slate-500", row: "bg-slate-50 border-slate-100" },
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="text-slate-400"><Ico.Alert /></div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Rate Limit Warnings</p>
        </div>
        {warnings.length > 0
          ? <Badge color="red">{warnings.length} active</Badge>
          : <Badge color="emerald"><Ico.Check /> All clear</Badge>
        }
      </div>

      {warnings.length === 0 ? (
        <div className="flex flex-col items-center py-8 gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
            <Ico.Check />
          </div>
          <p className="text-sm font-semibold text-slate-600">No active warnings</p>
          <p className="text-xs text-slate-400">Rate limits are within normal thresholds</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {warnings.map((w, i) => {
            const cl = sevCfg[w.severity] || sevCfg.warning;
            return (
              <div key={i} className={`rounded-xl border px-4 py-3 ${cl.row}`}>
                <div className="flex items-start gap-3">
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${cl.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className={`text-xs font-semibold ${cl.text}`}>{w.title}</p>
                      <Badge color={cl.badge}>{w.severity}</Badge>
                    </div>
                    <p className={`text-xs ${cl.sub}`}>{w.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};



/* ════════════════════════════════════  Main page  ════════════════════════════════════════ */

const QualityLimits = () => {
  const { phoneId } = useParams();
  const { getPhoneOverviewInfo } = useAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!phoneId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getPhoneOverviewInfo(phoneId); 
        setData(res);
      } catch (err) {
        console.error("QualityLimits API error:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [phoneId]);

  // Map API → your components
  const rating = data?.quality_rating || "High";
  const tier = (data?.messaging_tier || "TIER_1K").replace("TIER_", "");
  const dailyUsed = data?.daily_conversations_used || 0;
  const dailyLimit = data?.tier_limit || 0;
  const remaining = data?.remaining_capacity || 0;
  const usedPct = dailyLimit > 0 ? (dailyUsed / dailyLimit) * 100 : 0;
  const warnings = data?.warnings || [];

  if (loading) {
    return <SkeletonPage />;
  }

  if (!data) return <p>No data available</p>;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Quality & Messaging Limits</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor WhatsApp reliability metrics for this phone number</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
          Live
        </div>
      </div>

      {/* Campaign Readiness */}
      {/* <CampaignBanner rating={rating} usedPct={usedPct} warningCount={warnings.length} /> */}

      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <QualityRatingCard rating={rating} />
        <MessagingTierCard tier={tier} />
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <UsageCard used={dailyUsed} limit={dailyLimit} />
        <RateLimitWarnings warnings={warnings.map(w => ({ title: w.type, desc: w.message, severity: "warning" }))} />
      </div>
    </div>
  );
};

export default QualityLimits;